import React from "react";
import Modal from "components/ui/Modal";
import Select from "components/ui/Select";
import MultiSelect, { MultiSelectOption } from "components/ui/MultiSelect";
import { useAppLanguage } from "hooks/useAppLanguage";
import {
  UI_INPUT,
  UI_LABEL,
  UI_PRIMARY_BTN,
  UI_SECONDARY_BTN,
  UI_TEXTAREA,
} from "lib/uiClasses";
import { toastError, toastInfo } from "lib/toast";
import ConfirmDialog from "components/ui/ConfirmDialog";
import {
  AudienceOptions,
  CourseOption,
  CourseOptionsParams,
  CreateNotificationPayload,
  NamedOption,
  NotificationAudienceType,
  NotificationFilters,
  NotificationTargetType,
  notificationService,
} from "services/notificationService";
import { studentService, instructorService } from "services/entityService";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

const AUDIENCE_LABELS: Record<NotificationAudienceType, { ar: string; en: string }> = {
  all_students: { ar: "كل الطلاب", en: "All students" },
  all_instructors: { ar: "كل المحاضرين", en: "All instructors" },
  selected_students: { ar: "طلاب محددون", en: "Selected students" },
  selected_instructors: { ar: "محاضرون محددون", en: "Selected instructors" },
  course_students: { ar: "طلاب كورس معين", en: "Course students" },
  filtered_students: { ar: "طلاب حسب فلتر", en: "Filtered students" },
  filtered_instructors: { ar: "محاضرون حسب فلتر", en: "Filtered instructors" },
};

const TARGET_LABELS: Record<NotificationTargetType, { ar: string; en: string }> = {
  none: { ar: "بدون وجهة", en: "No target" },
  course: { ar: "كورس", en: "Course" },
  course_announcement: { ar: "إعلان كورس", en: "Course announcement" },
  course_chat: { ar: "شات كورس", en: "Course chat" },
  course_bundle: { ar: "باقة كورسات", en: "Course bundle" },
  url: { ar: "رابط", en: "URL" },
};

const emptyForm: CreateNotificationPayload = {
  title: "",
  body: "",
  audience_type: "all_students",
  selected_student_ids: [],
  selected_instructor_ids: [],
  filters: {},
  target_type: "none",
  target_data: {},
  send_now: true,
  scheduled_at: null,
};

const named = (list?: NamedOption[]) => (list ?? []).map((o) => ({ value: o.id, label: o.name }));

const dropEmpty = (filters: NotificationFilters): NotificationFilters => {
  const out: NotificationFilters = {};
  (Object.keys(filters) as (keyof NotificationFilters)[]).forEach((key) => {
    const value = filters[key];
    if (value !== null && value !== undefined && value !== ("" as unknown)) {
      (out as Record<string, unknown>)[key] = value;
    }
  });
  return out;
};

export default function CreateNotificationDialog({ open, onClose, onCreated }: Props) {
  const language = useAppLanguage();
  const isArabic = language === "ar";

  const [form, setForm] = React.useState<CreateNotificationPayload>(emptyForm);
  const [options, setOptions] = React.useState<AudienceOptions>({
    audience_types: [],
    target_types: [],
    academic: { stages: [], grades: [] },
    basic: { stages: [], grades: [] },
    courses: [],
  });
  const [saving, setSaving] = React.useState(false);
  const [previewCount, setPreviewCount] = React.useState<number | null>(null);
  const [previewSummary, setPreviewSummary] = React.useState<string>("");

  // Remote pickers for selected_* audiences.
  const [studentOpts, setStudentOpts] = React.useState<MultiSelectOption[]>([]);
  const [instructorOpts, setInstructorOpts] = React.useState<MultiSelectOption[]>([]);
  const [studentTerm, setStudentTerm] = React.useState("");
  const [instructorTerm, setInstructorTerm] = React.useState("");

  // Courses the *selected audience* can actually access (scoped course target).
  const [audienceCourses, setAudienceCourses] = React.useState<CourseOption[]>([]);
  const [coursesLoading, setCoursesLoading] = React.useState(false);
  const [emptyAudienceConfirmOpen, setEmptyAudienceConfirmOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setPreviewCount(null);
    setPreviewSummary("");
    setAudienceCourses([]);
    setEmptyAudienceConfirmOpen(false);
    notificationService
      .audienceOptions()
      .then((o) => setOptions(o))
      .catch(() =>
        setOptions({
          audience_types: [],
          target_types: [],
          academic: { stages: [], grades: [] },
          basic: { stages: [], grades: [] },
          courses: [],
        })
      );
  }, [open]);

  React.useEffect(() => {
    if (!open || form.audience_type !== "selected_students") return;
    const handle = setTimeout(() => {
      studentService
        .list({ term: studentTerm || undefined, limit: 30 })
        .then((res) =>
          setStudentOpts(
            (res.data ?? []).map((s: any) => ({ value: s.id, label: s.name ?? `#${s.id}` }))
          )
        )
        .catch(() => setStudentOpts([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [open, form.audience_type, studentTerm]);

  React.useEffect(() => {
    if (!open || form.audience_type !== "selected_instructors") return;
    const handle = setTimeout(() => {
      instructorService
        .list({ term: instructorTerm || undefined, limit: 30 })
        .then((res) =>
          setInstructorOpts(
            (res.data ?? []).map((s: any) => ({ value: s.id, label: s.name ?? `#${s.id}` }))
          )
        )
        .catch(() => setInstructorOpts([]));
    }, 250);
    return () => clearTimeout(handle);
  }, [open, form.audience_type, instructorTerm]);

  const filters = form.filters ?? {};

  const patchFilters = (patch: Partial<NotificationFilters>) => {
    setPreviewCount(null);
    setForm((p) => ({ ...p, filters: { ...(p.filters ?? {}), ...patch } }));
  };

  const setAudienceType = (next: NotificationAudienceType) => {
    setPreviewCount(null);
    setPreviewSummary("");
    setForm((p) => ({ ...p, audience_type: next, filters: {} }));
  };

  const setTargetData = (key: string, value: unknown) => {
    setPreviewCount(null);
    setForm((p) => ({ ...p, target_data: { ...(p.target_data ?? {}), [key]: value } }));
  };

  // Academic options (API returns `academic`; older payloads used `basic`).
  // Service normalizes both keys; keep defensive fallbacks anyway.
  const stageOpts = options.academic?.stages ?? options.basic?.stages ?? [];
  const gradeOpts = (options.academic?.grades ?? options.basic?.grades ?? []).filter(
    (g) => filters.stage_id != null && g.stage_id === filters.stage_id
  );

  const buildFilters = (): NotificationFilters => {
    if (form.audience_type === "course_students") {
      return filters.course_id ? { course_id: filters.course_id } : {};
    }
    return dropEmpty({
      stage_id: filters.stage_id ?? null,
      grade_id: filters.grade_id ?? null,
    });
  };

  const isFiltered =
    form.audience_type === "filtered_students" || form.audience_type === "filtered_instructors";

  const isStudentAudience =
    form.audience_type === "all_students" ||
    form.audience_type === "selected_students" ||
    form.audience_type === "course_students" ||
    form.audience_type === "filtered_students";

  const tt = form.target_type ?? "none";
  const isCourseTarget = tt === "course" || tt === "course_chat" || tt === "course_announcement";

  // Audience selection used to scope the course-target dropdown. Returns null
  // while the audience is still incomplete (so we don't query prematurely).
  const audienceCourseParams = (): CourseOptionsParams | null => {
    if (!isStudentAudience) return null;
    if (form.audience_type === "filtered_students") {
      return { audience_type: "filtered_students", filters: buildFilters() };
    }
    if (form.audience_type === "course_students") {
      if (!filters.course_id) return null;
      return { audience_type: "course_students", filters: { course_id: filters.course_id } };
    }
    if (form.audience_type === "selected_students") {
      const ids = (form.selected_student_ids ?? []).map(Number);
      if (ids.length === 0) return null;
      return { audience_type: "selected_students", selected_student_ids: ids };
    }
    return { audience_type: "all_students" };
  };

  const audienceCourseKey = JSON.stringify(audienceCourseParams());

  // Reload the course-target options whenever the audience or the (course-ish)
  // target type changes, so the dropdown only ever shows courses the audience
  // can actually access.
  React.useEffect(() => {
    if (!open || !isCourseTarget || !isStudentAudience) {
      setAudienceCourses([]);
      return;
    }
    const params = audienceCourseParams();
    if (!params) {
      setAudienceCourses([]);
      return;
    }
    let cancelled = false;
    setCoursesLoading(true);
    notificationService
      .courseOptions(params)
      .then((list) => {
        if (!cancelled) setAudienceCourses(list ?? []);
      })
      .catch(() => {
        if (!cancelled) setAudienceCourses([]);
      })
      .finally(() => {
        if (!cancelled) setCoursesLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isCourseTarget, isStudentAudience, audienceCourseKey]);

  // If the audience changes such that the chosen target course is no longer
  // accessible, clear it and invalidate the preview.
  React.useEffect(() => {
    if (!isCourseTarget || !isStudentAudience) return;
    const cid = form.target_data?.course_id;
    if (cid == null) return;
    if (!audienceCourses.some((c) => c.id === cid)) {
      setForm((p) => ({ ...p, target_data: { ...(p.target_data ?? {}), course_id: null } }));
      setPreviewCount(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceCourses]);

  const buildPayload = (): CreateNotificationPayload => {
    const payload: CreateNotificationPayload = {
      title: form.title.trim(),
      body: form.body.trim(),
      audience_type: form.audience_type,
      target_type: form.target_type,
      send_now: form.send_now,
      scheduled_at: form.send_now ? null : form.scheduled_at || null,
    };
    if (form.audience_type === "selected_students")
      payload.selected_student_ids = (form.selected_student_ids ?? []).map(Number);
    if (form.audience_type === "selected_instructors")
      payload.selected_instructor_ids = (form.selected_instructor_ids ?? []).map(Number);
    if (form.audience_type === "course_students" || isFiltered) {
      payload.filters = buildFilters();
    }
    if (form.target_type && form.target_type !== "none") {
      payload.target_data = form.target_data ?? {};
    }
    return payload;
  };

  const validate = (): string | null => {
    if (!form.title.trim()) return isArabic ? "العنوان مطلوب" : "Title is required";
    if (!form.body.trim()) return isArabic ? "النص مطلوب" : "Body is required";
    if (form.audience_type === "selected_students" && (form.selected_student_ids ?? []).length === 0)
      return isArabic ? "اختر طلابًا" : "Select students";
    if (
      form.audience_type === "selected_instructors" &&
      (form.selected_instructor_ids ?? []).length === 0
    )
      return isArabic ? "اختر محاضرين" : "Select instructors";
    if (form.audience_type === "course_students" && !filters.course_id)
      return isArabic ? "اختر كورسًا" : "Select a course";
    const td = form.target_data ?? {};
    if (
      (form.target_type === "course" ||
        form.target_type === "course_chat" ||
        form.target_type === "course_announcement") &&
      !td.course_id
    )
      return isArabic ? "اختر كورس الوجهة" : "Select target course";
    if (form.target_type === "course_announcement" && !td.announcement_id)
      return isArabic ? "أدخل معرف الإعلان" : "Enter announcement id";
    if (form.target_type === "course_bundle" && !td.bundle_id)
      return isArabic ? "أدخل معرف الباقة" : "Enter bundle id";
    if (form.target_type === "url" && !td.url)
      return isArabic ? "أدخل رابطًا صالحًا" : "Enter a valid URL";
    return null;
  };

  const nameOf = (list: NamedOption[] | undefined, id?: number | null) =>
    id == null ? null : (list ?? []).find((o) => o.id === id)?.name ?? `#${id}`;

  const targetCourseName = (): string | null => {
    const cid = form.target_data?.course_id;
    if (cid == null) return null;
    return (
      audienceCourses.find((c) => c.id === cid)?.title ??
      nameOf(options?.courses, cid as number) ??
      `#${cid}`
    );
  };

  const buildSummary = (): string => {
    const sep = " - ";
    const courseAccessClause = () => {
      if (!isCourseTarget) return null;
      const course = targetCourseName();
      if (!course) return null;
      return isArabic ? `لديهم صلاحية كورس ${course}` : `can access course ${course}`;
    };

    if (form.audience_type === "course_students") {
      const course = nameOf(options?.courses, filters.course_id);
      return `${isArabic ? "طلاب كورس" : "Course students"}${course ? sep + course : ""}`;
    }
    const parts: string[] = [];
    parts.push(
      form.audience_type === "filtered_instructors"
        ? isArabic
          ? "المحاضرون"
          : "Instructors"
        : isArabic
          ? "الطلاب"
          : "Students"
    );
    if (filters.stage_id || filters.grade_id) {
      const academicStages = options.academic?.stages ?? options.basic?.stages;
      const academicGrades = options.academic?.grades ?? options.basic?.grades;
      const s = nameOf(academicStages, filters.stage_id);
      const g = nameOf(academicGrades, filters.grade_id);
      [s, g].forEach((v) => v && parts.push(v));
    }
    const access = courseAccessClause();
    if (access) parts.push(access);
    return parts.join(sep);
  };

  const handlePreview = async () => {
    try {
      const payload = buildPayload();
      const res = await notificationService.previewAudience({
        audience_type: payload.audience_type,
        selected_student_ids: payload.selected_student_ids,
        selected_instructor_ids: payload.selected_instructor_ids,
        filters: payload.filters,
        target_type: payload.target_type,
        target_data: payload.target_data,
      });
      const count = res?.recipients_count ?? 0;
      setPreviewCount(count);
      setPreviewSummary(buildSummary());
      if (count === 0) {
        toastInfo(isArabic ? "لا يوجد مستلمين مطابقين لهذه الفلاتر" : "No recipients match these filters");
      } else {
        toastInfo(isArabic ? `عدد المستلمين: ${count}` : `Recipients: ${count}`);
      }
    } catch {
      toastError(isArabic ? "تعذّر حساب الجمهور" : "Could not preview audience");
    }
  };

  const submitNotification = async () => {
    setSaving(true);
    try {
      await notificationService.create(buildPayload());
      onCreated();
      onClose();
    } catch {
      // axios interceptor surfaces the error toast.
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      toastError(error);
      return;
    }
    if (previewCount === 0) {
      setEmptyAudienceConfirmOpen(true);
      return;
    }
    await submitNotification();
  };

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={isArabic ? "إرسال إشعار" : "Send notification"}
      size="lg"
      footer={
        <>
          <button type="button" className={UI_SECONDARY_BTN} onClick={onClose} disabled={saving}>
            {isArabic ? "إلغاء" : "Cancel"}
          </button>
          <button
            type="button"
            className={UI_SECONDARY_BTN}
            onClick={handlePreview}
            disabled={saving}
          >
            {isArabic ? "معاينة الجمهور" : "Preview audience"}
          </button>
          <button type="button" className={UI_PRIMARY_BTN} onClick={handleSubmit} disabled={saving}>
            {saving
              ? isArabic
                ? "جارٍ الإرسال..."
                : "Sending..."
              : form.send_now
                ? isArabic
                  ? "إرسال الآن"
                  : "Send now"
                : isArabic
                  ? "جدولة"
                  : "Schedule"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <label className={UI_LABEL}>{isArabic ? "العنوان" : "Title"}</label>
          <input
            className={UI_INPUT}
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            placeholder={isArabic ? "عنوان الإشعار" : "Notification title"}
          />
        </div>

        <div className="md:col-span-2">
          <label className={UI_LABEL}>{isArabic ? "النص" : "Body"}</label>
          <textarea
            className={UI_TEXTAREA}
            value={form.body}
            onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))}
            placeholder={isArabic ? "محتوى الإشعار" : "Notification message"}
          />
        </div>

        <div className="md:col-span-2">
          <Select
            label={isArabic ? "الجمهور" : "Audience"}
            value={form.audience_type}
            onChange={(next) => setAudienceType((next as NotificationAudienceType) ?? "all_students")}
            options={(Object.keys(AUDIENCE_LABELS) as NotificationAudienceType[]).map((k) => ({
              value: k,
              label: isArabic ? AUDIENCE_LABELS[k].ar : AUDIENCE_LABELS[k].en,
            }))}
            isArabic={isArabic}
            allowEmpty={false}
            clearable={false}
          />
        </div>

        {form.audience_type === "selected_students" && (
          <div className="md:col-span-2">
            <label className={UI_LABEL}>{isArabic ? "الطلاب" : "Students"}</label>
            <MultiSelect
              value={form.selected_student_ids ?? []}
              options={studentOpts}
              onChange={(v) => setForm((p) => ({ ...p, selected_student_ids: v.map(Number) }))}
              onSearchChange={setStudentTerm}
              searchTerm={studentTerm}
              placeholder={isArabic ? "ابحث عن طالب" : "Search students"}
            />
          </div>
        )}

        {form.audience_type === "selected_instructors" && (
          <div className="md:col-span-2">
            <label className={UI_LABEL}>{isArabic ? "المحاضرون" : "Instructors"}</label>
            <MultiSelect
              value={form.selected_instructor_ids ?? []}
              options={instructorOpts}
              onChange={(v) => setForm((p) => ({ ...p, selected_instructor_ids: v.map(Number) }))}
              onSearchChange={setInstructorTerm}
              searchTerm={instructorTerm}
              placeholder={isArabic ? "ابحث عن محاضر" : "Search instructors"}
            />
          </div>
        )}

        {form.audience_type === "course_students" && (
          <div className="md:col-span-2">
            <Select
              label={isArabic ? "الكورس" : "Course"}
              value={filters.course_id ?? null}
              onChange={(v) => patchFilters({ course_id: v === null || v === "" ? null : Number(v) })}
              options={named(options?.courses)}
              isArabic={isArabic}
              numeric
              searchable
              placeholder={isArabic ? "اختر الكورس" : "Select course"}
            />
          </div>
        )}

        {isFiltered && (
          <>
            <Select
              label={isArabic ? "المرحلة" : "Stage"}
              value={filters.stage_id ?? null}
              onChange={(v) =>
                patchFilters({
                  stage_id: v === null || v === "" ? null : Number(v),
                  grade_id: null,
                })
              }
              options={named(stageOpts)}
              isArabic={isArabic}
              numeric
              searchable
              placeholder={isArabic ? "أي مرحلة" : "Any stage"}
            />
            <Select
              label={isArabic ? "الصف / الفصل" : "Grade / Class"}
              value={filters.grade_id ?? null}
              onChange={(v) =>
                patchFilters({ grade_id: v === null || v === "" ? null : Number(v) })
              }
              options={named(gradeOpts)}
              isArabic={isArabic}
              numeric
              searchable
              disabled={filters.stage_id == null}
              placeholder={isArabic ? "أي صف" : "Any grade"}
            />
          </>
        )}

        <div className="md:col-span-2 mt-1 border-t border-gray-200 pt-3 dark:border-white/[0.08]">
          <Select
            label={isArabic ? "نوع الوجهة" : "Target type"}
            value={tt}
            onChange={(next) => {
              setPreviewCount(null);
              setForm((p) => ({
                ...p,
                target_type: (next as NotificationTargetType) ?? "none",
                target_data: {},
              }));
            }}
            options={(Object.keys(TARGET_LABELS) as NotificationTargetType[]).map((k) => ({
              value: k,
              label: isArabic ? TARGET_LABELS[k].ar : TARGET_LABELS[k].en,
            }))}
            isArabic={isArabic}
            allowEmpty={false}
            clearable={false}
          />
        </div>

        {isCourseTarget && (
          <div className="md:col-span-2">
            <Select
              label={isArabic ? "كورس الوجهة" : "Target course"}
              value={(form.target_data?.course_id as number) ?? null}
              onChange={(v) => setTargetData("course_id", v === null || v === "" ? null : Number(v))}
              options={
                isStudentAudience
                  ? audienceCourses.map((c) => ({
                      value: c.id,
                      label: isArabic
                        ? `${c.title} (${c.students_count} طالب)`
                        : `${c.title} (${c.students_count})`,
                    }))
                  : named(options?.courses)
              }
              isArabic={isArabic}
              numeric
              searchable
              disabled={coursesLoading}
              placeholder={
                coursesLoading
                  ? isArabic
                    ? "جارٍ تحميل الكورسات..."
                    : "Loading courses..."
                  : isArabic
                    ? "اختر الكورس"
                    : "Select course"
              }
            />
            {isStudentAudience && !coursesLoading && audienceCourses.length === 0 && (
              <p className="mt-1 text-sm text-amber-600 dark:text-amber-300">
                {isArabic
                  ? "لا توجد كورسات مطابقة لهذا الجمهور"
                  : "No courses match this audience"}
              </p>
            )}
          </div>
        )}

        {tt === "course_announcement" && (
          <div>
            <label className={UI_LABEL}>{isArabic ? "معرف الإعلان" : "Announcement ID"}</label>
            <input
              className={UI_INPUT}
              type="number"
              value={(form.target_data?.announcement_id as number) ?? ""}
              onChange={(e) =>
                setTargetData("announcement_id", e.target.value ? Number(e.target.value) : null)
              }
            />
          </div>
        )}

        {tt === "course_bundle" && (
          <div>
            <label className={UI_LABEL}>{isArabic ? "معرف الباقة" : "Bundle ID"}</label>
            <input
              className={UI_INPUT}
              type="number"
              value={(form.target_data?.bundle_id as number) ?? ""}
              onChange={(e) =>
                setTargetData("bundle_id", e.target.value ? Number(e.target.value) : null)
              }
            />
          </div>
        )}

        {tt === "url" && (
          <div className="md:col-span-2">
            <label className={UI_LABEL}>{isArabic ? "الرابط" : "URL"}</label>
            <input
              className={UI_INPUT}
              type="url"
              value={(form.target_data?.url as string) ?? ""}
              onChange={(e) => setTargetData("url", e.target.value)}
              placeholder="https://..."
            />
          </div>
        )}

        <div className="md:col-span-2 mt-1 flex flex-wrap items-end gap-4 border-t border-gray-200 pt-3 dark:border-white/[0.08]">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={!!form.send_now}
              onChange={(e) => setForm((p) => ({ ...p, send_now: e.target.checked }))}
            />
            {isArabic ? "إرسال الآن" : "Send now"}
          </label>
          {!form.send_now && (
            <div>
              <label className={UI_LABEL}>{isArabic ? "موعد الجدولة" : "Schedule at"}</label>
              <input
                className={UI_INPUT}
                type="datetime-local"
                value={form.scheduled_at ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, scheduled_at: e.target.value }))}
              />
            </div>
          )}
        </div>

        {previewCount !== null && (
          <div
            className={`md:col-span-2 rounded-lg px-3 py-2 text-sm ${
              previewCount === 0
                ? "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                : "bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
            }`}
          >
            <div className="font-semibold">
              {previewCount === 0
                ? isArabic
                  ? "لا يوجد مستلمين مطابقين لهذه الفلاتر"
                  : "No recipients match these filters"
                : isArabic
                  ? `عدد المستلمين: ${previewCount}`
                  : `Recipients: ${previewCount}`}
            </div>
            {previewSummary && <div className="mt-0.5 opacity-80">{previewSummary}</div>}
          </div>
        )}
      </div>
    </Modal>

    <ConfirmDialog
      open={emptyAudienceConfirmOpen}
      onClose={() => setEmptyAudienceConfirmOpen(false)}
      onConfirm={submitNotification}
      variant="primary"
      title={isArabic ? "إرسال بدون مستلمين؟" : "Send with no recipients?"}
      description={
        isArabic
          ? "لا يوجد مستلمين مطابقين لهذه الفلاتر. هل تريد المتابعة على أي حال؟"
          : "No recipients match these filters. Send anyway?"
      }
      confirmLabel={isArabic ? "إرسال على أي حال" : "Send anyway"}
    />
    </>
  );
}
